import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { OnboardingAudience } from "@/lib/onboarding/tours";

export interface SetupTask {
  id: string;
  label: string;
  done: boolean;
  route?: string;
  hint?: string;
}

/**
 * Setup progress derived from genuine system state — never manual tick boxes.
 * Extend by adding a loader for a new audience.
 */
export function useSetupProgress(audience: OnboardingAudience | null) {
  const [tasks, setTasks] = useState<SetupTask[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!audience) {
      setTasks([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const { data: auth } = await supabase.auth.getUser();
    const uid = auth.user?.id;
    if (!uid) {
      setTasks([]);
      setLoading(false);
      return;
    }

    if (audience === "homeowner") {
      const [customerRes, propertyRes, jobRes, proposalRes] = await Promise.all([
        supabase
          .from("customers")
          .select("full_name, mobile, preferences_updated_at")
          .eq("user_id", uid)
          .maybeSingle(),
        supabase.from("properties").select("id").eq("user_id", uid).limit(1),
        supabase.from("jobs").select("id").limit(1),
        supabase.from("customer_proposals").select("id").limit(1),
      ]);

      const c = customerRes.data;
      setTasks([
        {
          id: "contact",
          label: "Contact details confirmed",
          done: !!(c?.full_name && c?.mobile),
          route: "/customer/account",
          hint: "Your name and mobile number let us confirm appointments.",
        },
        {
          id: "preferences",
          label: "Communication preferences set",
          done: !!c?.preferences_updated_at,
          route: "/customer/account",
          hint: "Tell us how you prefer to be contacted.",
        },
        {
          id: "property",
          label: "Property information added",
          done: (propertyRes.data?.length ?? 0) > 0,
          route: "/customer/property",
        },
        {
          id: "project",
          label: "Project under way",
          done: (jobRes.data?.length ?? 0) > 0,
          route: "/customer",
          hint: "Created by EvoHome Improvements once your enquiry progresses.",
        },
        {
          id: "proposal",
          label: "Proposal available to review",
          done: (proposalRes.data?.length ?? 0) > 0,
          route: "/customer",
          hint: "Appears once your consultant has prepared and approved it.",
        },
      ]);
      setLoading(false);
      return;
    }

    // Installer
    const [profileRes, complianceRes] = await Promise.all([
      supabase
        .from("installer_profiles")
        .select("company_name, phone, logo_url, trades, service_categories, bio_public")
        .eq("user_id", uid)
        .maybeSingle(),
      supabase.from("installer_compliance_documents").select("id").limit(1),
    ]);
    const p = profileRes.data as
      | {
          company_name: string | null;
          phone: string | null;
          logo_url: string | null;
          trades: string[] | null;
          service_categories: string[] | null;
          bio_public: string | null;
        }
      | null;

    setTasks([
      {
        id: "business",
        label: "Business details completed",
        done: !!(p?.company_name && p?.phone),
        route: "/portal/installer/profile",
      },
      {
        id: "logo",
        label: "Company logo uploaded",
        done: !!p?.logo_url,
        route: "/portal/installer/profile",
        hint: "Used on invoices your business issues.",
      },
      {
        id: "trades",
        label: "Trades and services set",
        done: (p?.trades?.length ?? 0) > 0 || (p?.service_categories?.length ?? 0) > 0,
        route: "/portal/installer/profile",
      },
      {
        id: "bio",
        label: "Company bio supplied",
        done: !!(p?.bio_public && p.bio_public.trim().length > 0),
        route: "/portal/installer/profile",
        hint: "Shown to a homeowner only after EvoHome Improvements approves it.",
      },
      {
        id: "compliance",
        label: "Compliance evidence supplied",
        done: (complianceRes.data?.length ?? 0) > 0,
        route: "/portal/installer/compliance",
      },
    ]);
    setLoading(false);
  }, [audience]);

  useEffect(() => {
    void load();
  }, [load]);

  const completed = tasks.filter((t) => t.done).length;
  return { tasks, completed, total: tasks.length, loading, reload: load };
}
